import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  deadLetterExchange,
  getExchange,
  requestExchangeReplay,
  requestExchangeRetry,
} from '../api/exchanges';
import { flowsApi } from '../api/flows';
import { getOperationsSnapshot, type ObservabilityDashboardSnapshot } from '../api/observability';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { CopyButton } from '../components/CopyButton';
import { PollingIndicator } from '../components/PollingIndicator';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { redactSensitiveValue } from '../components/PayloadViewer';
import { RelatedLinks } from '../components/RelatedLinks';
import { Timeline } from '../components/Timeline';
import { KpiTrend } from '../components/Sparkline';
import { documentsByCorrelationLink, executionsByCorrelationLink } from '../lib/crossLinks';
import { buildExchangeTimelineEvents } from '../lib/timeline';
import { bucketCountsByTime, ONE_HOUR_MS } from '../lib/timeBuckets';
import { usePermission, type Permission } from '../hooks/usePermission';
import type {
  ConnectorInstanceRecord,
  ExchangeDetails,
  ExchangeSummary,
  FlowExecutionRecord,
  FlowRecord,
  PartnerRecord,
} from '../types/api';

type PayloadFormat = 'json' | 'xml' | 'edifact';
type DlqAction = 'retry' | 'replay' | 'quarantine' | 'annotate';
type FlowAction = 'approve' | 'activate' | 'rollback';

interface OperationsSnapshot {
  exchanges: ExchangeSummary[];
  flows: FlowRecord[];
  flowExecutions: FlowExecutionRecord[];
  partners: PartnerRecord[];
  connectors: ConnectorInstanceRecord[];
  metrics: string;
  observability: ObservabilityDashboardSnapshot | null;
}

// Deliberately longer than the 15-30s default other pages use: a full load() here fans out to
// exchanges, connectors, partners, flows, flow executions, observability, and prometheus metrics
// in one shot, so a shorter interval would multiply an already-heavy per-page fetch cost.
const OPERATIONS_CENTER_POLL_INTERVAL_MS = 45_000;

const permissionPosture: Array<{ permission: Permission; access: string; controls: string }> = [
  { permission: 'integration.execution.retry', access: 'Runtime recovery', controls: 'Retry failed exchange delivery' },
  { permission: 'integration.execution.replay', access: 'Runtime recovery', controls: 'Replay an exchange after configuration fixes' },
  { permission: 'integration.dlq.requeue', access: 'DLQ governance', controls: 'Quarantine or annotate dead-lettered work' },
  { permission: 'flow.operations.execute', access: 'Flow lifecycle', controls: 'Approve, activate, and rollback flow versions' },
];

export function OperationsCenterPage() {
  const { search } = useLocation();
  const [snapshot, setSnapshot] = useState<OperationsSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollingPaused, setPollingPaused] = useState(false);
  const loadingRef = useRef(false);

  async function loadOverview() {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setError(null);
    try {
      setSnapshot(await getOperationsSnapshot());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to load the operational overview.');
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  }

  useEffect(() => { void loadOverview(); }, []);
  useEffect(() => {
    if (pollingPaused) return undefined;
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') void loadOverview();
    }, OPERATIONS_CENTER_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [pollingPaused]);

  if (isLoading && !snapshot) return <LoadingState label="Loading operational overview..." />;

  const global = buildGlobalDashboard(snapshot);
  const trends = buildExchangeTrendBuckets(snapshot);
  const alerts = buildAlerts(snapshot, global).slice(0, 5);
  const recentFailures = (snapshot?.exchanges ?? [])
    .filter((exchange) => ['FAILED', 'WAITING_RETRY', 'DEAD_LETTERED'].includes(exchange.status))
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
    .slice(0, 5);

  return (
    <div className="page-grid operations-center operations-overview">
      <section className="panel ops-command-header">
        <div>
          <span>OPERATIONS OVERVIEW</span>
          <h1>Integration Command Center</h1>
          <p>See current service health, prioritize unresolved work, and continue investigation in the dedicated operational consoles.</p>
        </div>
        <div className="panel-actions">
          <PollingIndicator paused={pollingPaused} onToggle={() => setPollingPaused((paused) => !paused)} />
          <button type="button" onClick={() => void loadOverview()}>Refresh</button>
        </div>
      </section>

      {error ? <ErrorState message={error} onRetry={loadOverview} /> : null}

      {snapshot ? <>
      <section className="operations-card-grid command-center-kpis" aria-label="Current operational health">
        <Kpi label="Failures" value={global.failures} detail="Failed exchanges in the current runtime sample." tone={global.failures ? 'danger' : 'good'} trend={<KpiTrend buckets={trends.failures} goodDirection="down" periodLabel="prior hour" />} />
        <Kpi label="Needs recovery" value={global.deadLettered + global.retries} detail="Dead-lettered or waiting-retry exchanges requiring disposition." tone={global.deadLettered + global.retries ? 'danger' : 'good'} />
        <Kpi label="SLA breaches" value={global.slaBreaches} detail="Failed or in-flight work beyond the operating threshold." tone={global.slaBreaches ? 'danger' : 'good'} />
        <Kpi label="Partner availability" value={`${global.partnerAvailability}%`} detail="Active partners and healthy connectors in the current catalog." tone={global.partnerAvailability < 95 ? 'warning' : 'good'} />
        <Kpi label="P95 latency" value={`${snapshot?.observability?.operational.p95LatencyMs ?? 0} ms`} detail="Backend-observed exchange latency." tone={(snapshot?.observability?.operational.p95LatencyMs ?? 0) > 30_000 ? 'warning' : 'neutral'} />
      </section>

      <section className="page-grid two-columns command-center-priority-grid">
        <section className="panel">
          <div className="panel-header">
            <div><h2>Action required</h2><p>Highest-priority active signals from the current operational sample.</p></div>
            <Link className="button-link" to={{ pathname: '/dlq', search }}>Open Recovery</Link>
          </div>
          <div className="stack-list">
            {alerts.length ? alerts.map((alert) => (
              <div className={`stack-item alert-${alert.severity}`} key={alert.id}>
                <strong>{alert.title}</strong><span>{alert.description}</span>
              </div>
            )) : <EmptyState label="No active operational alerts." />}
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div><h2>Recent failures</h2><p>Open a record to investigate its complete runtime trace.</p></div>
            <Link className="button-link" to={{ pathname: '/executions', search }}>Open Monitoring</Link>
          </div>
          <div className="command-center-failure-list">
            {recentFailures.length ? recentFailures.map((exchange) => (
              <Link className="command-center-failure" key={exchange.id} to={{ pathname: `/operations/exchanges/${exchange.id}`, search }}>
                <span><strong>{exchange.documentType}</strong><small>{formatRuntimeSummary(exchange.correlationId ?? exchange.id)}</small></span>
                <span><StatusBadge value={exchange.status} /><time dateTime={exchange.createdAt}>{formatOverviewTime(exchange.createdAt)}</time></span>
              </Link>
            )) : <EmptyState label="No recent failed exchanges." />}
          </div>
        </section>
      </section>

      <section className="panel command-center-destinations">
        <div className="panel-header"><div><h2>Continue investigation</h2><p>Use the focused console for the task you need to complete.</p></div></div>
        <div className="operations-card-grid command-center-links">
          <OperationalDestination title="Monitoring" detail="Search executions, inspect payloads, and follow correlations." to="/executions" search={search} />
          <OperationalDestination title="Recovery" detail="Triage dead-lettered work and perform controlled recovery." to="/dlq" search={search} />
          <OperationalDestination title="Exchange runtime" detail="Inspect end-to-end exchange traces and worker history." to="/operations/exchanges" search={search} />
          <OperationalDestination title="Flow engine" detail="Review flow runtime and lifecycle operations." to="/operations/flows" search={search} />
        </div>
      </section>
      </> : null}
    </div>
  );
}

function LegacyOperationsCenterPage() {
  const { search } = useLocation();
  const [snapshot, setSnapshot] = useState<OperationsSnapshot | null>(null);
  const [selectedExchange, setSelectedExchange] = useState<ExchangeDetails | null>(null);
  const [selectedFlow, setSelectedFlow] = useState<FlowRecord | null>(null);
  const [payloadFormat, setPayloadFormat] = useState<PayloadFormat>('json');
  const [redactPayload, setRedactPayload] = useState(true);
  const [diffMode, setDiffMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSelectingExchange, setIsSelectingExchange] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<ConfirmDialogState | null>(null);
  const [pollingPaused, setPollingPaused] = useState(false);
  const operationReasonRef = useRef('');
  const isLoadingRef = useRef(false);
  const canRetryExchange = usePermission('integration.execution.retry');
  const canReplayExchange = usePermission('integration.execution.replay');
  const canManageDeadLetter = usePermission('integration.dlq.requeue');
  const canDeployFlow = usePermission('flow.operations.execute');
  const canRevealSensitive = usePermission('integration.payload.revealSensitive');

  async function load() {
    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const nextSnapshot = await getOperationsSnapshot();
      setSnapshot(nextSnapshot);
      setSelectedFlow((current) => {
        const refreshed = nextSnapshot.flows.find((f) => f.id === current?.id);
        return refreshed ?? current ?? nextSnapshot.flows[0] ?? null;
      });
      // Always re-fetch exchange detail to keep the panel current after any load()
      const exchangeId = selectedExchange?.id ?? nextSnapshot.exchanges[0]?.id;
      if (exchangeId) {
        const detail = await getExchange(exchangeId).catch(() => null);
        setSelectedExchange(detail);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to load operations center.');
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const isOperationInFlight = isSubmitting || isSelectingExchange || Boolean(dialog);

  // Bespoke polling (rather than useAsyncData's pollIntervalMs) because this page already owns a
  // custom load()/useEffect pair instead of using the hook. Mirrors the same guards: skip a tick
  // if a mutation/dialog is in flight, a load() is already in flight, or the tab is hidden.
  useEffect(() => {
    if (pollingPaused) return undefined;

    const interval = window.setInterval(() => {
      if (isOperationInFlight) return;
      if (isLoadingRef.current) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      void load();
    }, OPERATIONS_CENTER_POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [pollingPaused, isOperationInFlight]);

  const global = useMemo(() => buildGlobalDashboard(snapshot), [snapshot]);
  const trends = useMemo(() => buildExchangeTrendBuckets(snapshot), [snapshot]);
  const partnerHealth = useMemo(() => buildPartnerHealth(snapshot), [snapshot]);
  const alerts = useMemo(() => buildAlerts(snapshot, global), [snapshot, global]);
  const selectedFlowExecutions = useMemo(
    () => snapshot?.flowExecutions.filter((execution) => !selectedFlow || execution.flowId === selectedFlow.id) ?? [],
    [selectedFlow, snapshot],
  );
  const payload = selectedExchange?.executionRequests[0]?.payload ?? selectedFlowExecutions[0]?.payloadSnapshot ?? {};
  const payloadCompare = selectedExchange?.executionRequests[0]?.result?.details ?? selectedFlowExecutions[0]?.resultSnapshot ?? {};

  function dlqDisabledReason(action: DlqAction): string | null {
    if (isSubmitting) return 'An operation is already running.';
    if (!selectedExchange) return 'Select an exchange before using recovery controls.';
    if (action === 'retry' && !canRetryExchange) return 'You need exchange retry permission.';
    if (action === 'replay' && !canReplayExchange) return 'You need exchange replay permission.';
    if ((action === 'quarantine' || action === 'annotate') && !canManageDeadLetter) return 'You need dead-letter management permission.';
    return null;
  }

  function flowDisabledReason(action: FlowAction): string | null {
    if (isSubmitting) return 'An operation is already running.';
    if (!selectedFlow) return 'Select a flow before using lifecycle controls.';
    if (!canDeployFlow) return `You need flow deploy permission to ${action} flows.`;
    const state = selectedFlow.selectedVersion.lifecycleState;
    if (action === 'approve' && state !== 'DRAFT') return `Cannot deploy: version is already ${state}.`;
    if (action === 'activate' && state !== 'APPROVED') return `Cannot activate: version is ${state}, expected APPROVED.`;
    if (action === 'rollback' && state !== 'ACTIVE') return `Cannot roll back: version is ${state}, expected ACTIVE.`;
    return null;
  }

  async function selectExchange(id: string) {
    setIsSelectingExchange(true);
    setError(null);
    try {
      setSelectedExchange(await getExchange(id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to load exchange trace.');
    } finally {
      setIsSelectingExchange(false);
    }
  }

  function requestDlqAction(action: DlqAction) {
    if (!selectedExchange || dlqDisabledReason(action)) return;
    const defaultReason = actionLabel(action);
    operationReasonRef.current = defaultReason;
    setDialog({
      title: actionLabel(action),
      message: `This will submit a persisted ${action} request for exchange ${selectedExchange.id}. Review impact before continuing.`,
      confirmLabel: actionLabel(action),
      risk: action === 'quarantine' ? 'high' : 'medium',
      detailLabel: action === 'annotate' || action === 'quarantine' ? 'Operator annotation' : 'Reason',
      detailValue: defaultReason,
      detailRequired: true,
      onDetailChange: (value) => {
        operationReasonRef.current = value;
        setDialog((current) => current ? { ...current, detailValue: value } : current);
      },
      onConfirm: () => runDlqAction(action),
    });
  }

  async function runDlqAction(action: DlqAction) {
    if (!selectedExchange) return;
    const reason = operationReasonRef.current.trim() || actionLabel(action);
    setIsSubmitting(true);
    setError(null);
    try {
      const updated = action === 'retry'
        ? await requestExchangeRetry(selectedExchange.id, reason)
        : action === 'replay'
          ? await requestExchangeReplay(selectedExchange.id, reason)
          : await deadLetterExchange(selectedExchange.id, reason || 'Operator DLQ review', {
            quarantineMode: action === 'quarantine',
            operatorComment: reason,
          });
      setSelectedExchange(updated);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to complete DLQ action.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function requestTransitionFlow(action: FlowAction) {
    if (!selectedFlow || flowDisabledReason(action)) return;
    const requireText = action === 'activate' ? 'ACTIVATE' : action === 'rollback' ? 'ROLLBACK' : undefined;
    setDialog({
      title: `${action === 'approve' ? 'Deploy' : action === 'activate' ? 'Activate' : 'Rollback'} flow`,
      message: `This will ${action} version ${selectedFlow.selectedVersion.versionLabel} for ${selectedFlow.name}.`,
      confirmLabel: action === 'approve' ? 'Deploy flow' : action === 'activate' ? 'Activate flow' : 'Rollback flow',
      risk: action === 'approve' ? 'medium' : 'high',
      requireText,
      onConfirm: () => transitionFlow(action),
    });
  }

  async function transitionFlow(action: FlowAction) {
    if (!selectedFlow) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const versionId = selectedFlow.selectedVersion.id;
      const updated = action === 'approve'
        ? await flowsApi.approve(selectedFlow.id, versionId)
        : action === 'activate'
          ? await flowsApi.activate(selectedFlow.id, versionId)
          : await flowsApi.rollback(selectedFlow.id, versionId);
      setSelectedFlow(updated);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to transition flow.');
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading && !snapshot) return <LoadingState label="Loading operations center..." />;

  return (
    <div className="page-grid operations-center">
      <section className="panel ops-command-header">
        <div>
          <span>ENTERPRISE EAI OPERATIONS</span>
          <h1>Integration Command Center</h1>
          <p>Runtime command, DLQ triage, partner health, flow deployment, payload analysis, alerting, RBAC, and observability in one shell-integrated console.</p>
        </div>
        <div className="panel-actions">
          <PollingIndicator paused={pollingPaused} onToggle={() => setPollingPaused((paused) => !paused)} />
          <button type="button" onClick={() => void load()} disabled={isSubmitting}>Refresh</button>
          <Link className="button-link" to={{ pathname: '/operations/exchanges', search }}>Exchange runtime</Link>
          <Link className="button-link" to={{ pathname: '/operations/flows', search }}>Flow engine</Link>
        </div>
      </section>

      {error ? <ErrorState message={error} onRetry={load} /> : null}

      <section className="operations-card-grid ops-kpi-grid">
        <Kpi
          label="Exchanges/sec"
          value={global.exchangesPerSecond.toFixed(2)}
          detail="Observed over the latest 60-minute exchange window."
          tone="neutral"
          trend={<KpiTrend buckets={trends.volume} goodDirection="neutral" periodLabel="prior hour" />}
        />
        <Kpi
          label="Failures"
          value={global.failures}
          detail="Failed exchanges visible in the current runtime sample."
          tone={global.failures > 0 ? 'danger' : 'good'}
          trend={<KpiTrend buckets={trends.failures} goodDirection="down" periodLabel="prior hour" />}
        />
        <Kpi
          label="Retries"
          value={global.retries}
          detail="Retry requests and waiting retry exchanges."
          tone={global.retries > 0 ? 'warning' : 'neutral'}
          trend={<KpiTrend buckets={trends.retries} goodDirection="down" periodLabel="prior hour" />}
        />
        <Kpi
          label="DLQ"
          value={global.deadLettered}
          detail="Dead-lettered exchanges requiring operator disposition."
          tone={global.deadLettered > 0 ? 'danger' : 'good'}
          trend={<KpiTrend buckets={trends.deadLettered} goodDirection="down" periodLabel="prior hour" />}
        />
        <Kpi label="SLA breaches" value={global.slaBreaches} detail="Failed or in-flight work beyond the operating SLA threshold." tone={global.slaBreaches > 0 ? 'danger' : 'good'} />
        <Kpi label="Partner availability" value={`${global.partnerAvailability}%`} detail="Active partners and healthy connectors in the catalog sample." tone={global.partnerAvailability < 95 ? 'warning' : 'good'} />
        <Kpi label="P95 latency" value={`${snapshot?.observability?.operational.p95LatencyMs ?? 0} ms`} detail="Backend-observed exchange latency histogram." tone={(snapshot?.observability?.operational.p95LatencyMs ?? 0) > 30000 ? 'warning' : 'neutral'} />
        <Kpi label="Trace samples" value={snapshot?.observability?.tracing.sampledTraceCount ?? 0} detail="Historical spans available for reconstruction." tone="neutral" />
      </section>

      <section className="page-grid two-columns ops-wide-left">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Exchange timeline</h2>
              <p>End-to-end trace with connector executions, retries, outbox events, and payload versions.</p>
            </div>
          </div>
          <div className="ops-split">
            <div className="ops-list">
              {snapshot?.exchanges.length ? snapshot.exchanges.map((exchange) => (
                <div
                  key={exchange.id}
                  role="button"
                  tabIndex={isSelectingExchange ? -1 : 0}
                  aria-disabled={isSelectingExchange}
                  className={`ops-list-item${selectedExchange?.id === exchange.id ? ' active' : ''}`}
                  onClick={() => { if (!isSelectingExchange) void selectExchange(exchange.id); }}
                  onKeyDown={(event) => {
                    if (isSelectingExchange) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      void selectExchange(exchange.id);
                    }
                  }}
                >
                  <strong>{exchange.documentType}</strong>
                  <span
                    className="inline-copy"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <span className="mono">{exchange.correlationId ?? exchange.id}</span>
                    <CopyButton value={exchange.correlationId ?? exchange.id} label="Copy correlation ID" />
                    {exchange.correlationId ? (
                      <RelatedLinks links={[
                        documentsByCorrelationLink(exchange.correlationId, search),
                        executionsByCorrelationLink(exchange.correlationId, search),
                      ]}
                      />
                    ) : null}
                  </span>
                  <StatusBadge value={exchange.status} />
                </div>
              )) : <EmptyState label="No exchanges found." />}
            </div>
            {selectedExchange ? (
              <Timeline events={buildExchangeTimelineEvents(selectedExchange)} />
            ) : (
              <EmptyState label="Select an exchange to render its trace." />
            )}
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>DLQ management</h2>
              <p>Inspect, replay, quarantine, and annotate failed or dead-lettered work.</p>
            </div>
            <div className="panel-actions">
              <Link className="button-link" to={{ pathname: '/dlq', search }}>Open full DLQ console &rarr;</Link>
            </div>
          </div>
          {selectedExchange ? (
            <>
              <div className="summary-grid compact-summary">
                <div><strong>Exchange</strong><span className="mono">{selectedExchange.id}</span></div>
                <div><strong>Status</strong><span><StatusBadge value={selectedExchange.status} /></span></div>
                <div><strong>Retries</strong><span>{selectedExchange.retryAttempts.length}</span></div>
                <div><strong>Replay</strong><span>{selectedExchange.replayRequests.length}</span></div>
              </div>
              <div className="panel-actions">
                {(['retry', 'replay', 'quarantine', 'annotate'] as const).map((action) => {
                  const reason = dlqDisabledReason(action);
                  return (
                    <span className="disabled-action-wrap" key={action}>
                      <button type="button" disabled={Boolean(reason)} title={reason ?? undefined} onClick={() => requestDlqAction(action)}>
                        {actionLabel(action)}
                      </button>
                      {reason ? <small>{reason}</small> : null}
                    </span>
                  );
                })}
              </div>
              <div className="stack-list">
                <div className="stack-item">
                  <strong>{selectedExchange.deadLetterEntry?.reason ?? 'No DLQ record'}</strong>
                  <span>{selectedExchange.deadLetterEntry?.operatorComment ?? 'No operator annotation has been recorded.'}</span>
                </div>
              </div>
            </>
          ) : <EmptyState label="Select an exchange to manage DLQ disposition." />}
        </section>
      </section>

      <section className="page-grid two-columns">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Partner health</h2>
              <p>Availability, latency, failures, and certificate expiration posture.</p>
            </div>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Partner</th>
                <th>Uptime</th>
                <th>Latency</th>
                <th>Failures</th>
                <th>Certificate</th>
              </tr>
            </thead>
            <tbody>
              {partnerHealth.map((partner) => (
                <tr key={partner.code}>
                  <td>
                    <div className="table-primary">
                      <span>{partner.name}</span>
                      <span>{partner.code}</span>
                    </div>
                  </td>
                  <td>{partner.uptime}%</td>
                  <td>{partner.latencyMs} ms</td>
                  <td>{partner.failures}</td>
                  <td><StatusBadge value={partner.certificateStatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Flow operations</h2>
              <p>Deployment, rollback, activation, and runtime status.</p>
            </div>
          </div>
          <div className="ops-flow-grid">
            <div className="ops-list">
              {snapshot?.flows.length ? snapshot.flows.map((flow) => (
                <button
                  type="button"
                  key={flow.id}
                  className={`ops-list-item${selectedFlow?.id === flow.id ? ' active' : ''}`}
                  onClick={() => setSelectedFlow(flow)}
                >
                  <strong>{flow.name}</strong>
                  <span>{flow.selectedVersion.lifecycleState}</span>
                </button>
              )) : <EmptyState label="No flows found." />}
            </div>
            <div className="stack-list">
              <div className="stack-item">
                <strong>{selectedFlow?.code ?? 'No flow selected'}</strong>
                <span>Active version {selectedFlow?.activeVersionId ?? 'none'}</span>
                <span>Runtime executions {selectedFlowExecutions.length}</span>
              </div>
              <div className="panel-actions">
                {(['approve', 'activate', 'rollback'] as const).map((action) => {
                  const reason = flowDisabledReason(action);
                  return (
                    <span className="disabled-action-wrap" key={action}>
                      <button type="button" disabled={Boolean(reason)} title={reason ?? undefined} onClick={() => requestTransitionFlow(action)}>
                        {action === 'approve' ? 'Deploy' : action === 'activate' ? 'Activate' : 'Rollback'}
                      </button>
                      {reason ? <small>{reason}</small> : null}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </section>

      <section className="page-grid two-columns ops-wide-left">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Payload viewer</h2>
              <p>Redacted JSON, XML, EDIFACT formatting, and diff inspection.</p>
            </div>
            <div className="panel-actions">
              <select value={payloadFormat} onChange={(event) => setPayloadFormat(event.target.value as PayloadFormat)} aria-label="Payload format">
                <option value="json">JSON</option>
                <option value="xml">XML</option>
                <option value="edifact">EDIFACT</option>
              </select>
              {canRevealSensitive ? (
                <label className="inline-check"><input type="checkbox" checked={redactPayload} onChange={(event) => setRedactPayload(event.target.checked)} /> Redact</label>
              ) : null}
              <label className="inline-check"><input type="checkbox" checked={diffMode} onChange={(event) => setDiffMode(event.target.checked)} /> Diff</label>
            </div>
          </div>
          <PayloadViewer value={payload} compare={payloadCompare} format={payloadFormat} redact={!canRevealSensitive || redactPayload} diff={diffMode} />
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Alerting</h2>
              <p>SLA violations, connector failures, and runtime degradation.</p>
            </div>
          </div>
          <div className="stack-list">
            {alerts.length ? alerts.map((alert) => (
              <div className={`stack-item alert-${alert.severity}`} key={alert.id}>
                <strong>{alert.title}</strong>
                <span>{alert.description}</span>
              </div>
            )) : <EmptyState label="No active alerts in the current sample." />}
          </div>
        </section>
      </section>

      <section className="page-grid two-columns">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Permission posture</h2>
              <p>Current identity permissions that enable recovery and flow lifecycle controls.</p>
            </div>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Permission</th>
                <th>Access</th>
                <th>Controls</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {permissionPosture.map((item) => {
                const granted = permissionGranted(item.permission, {
                  canRetryExchange,
                  canReplayExchange,
                  canManageDeadLetter,
                  canDeployFlow,
                });
                return (
                <tr key={item.permission} className={granted ? 'selected-row' : undefined}>
                  <td className="mono">{item.permission}</td>
                  <td>{item.access}</td>
                  <td>{item.controls}</td>
                  <td><StatusBadge value={granted ? 'GRANTED' : 'DENIED'} /></td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Observability</h2>
              <p>Prometheus, Grafana, and OpenTelemetry integration points.</p>
            </div>
          </div>
          <div className="observability-grid">
            <ObservabilityCard name="Prometheus" status={snapshot?.metrics ? 'CONNECTED' : 'UNAVAILABLE'} detail={`${parseMetricCount(snapshot?.metrics ?? '')} metric families scraped.`} />
            <ObservabilityCard name="Grafana" status="READY" detail={readRuntimeLink('VITE_GRAFANA_URL', 'Dashboard URL can be supplied by runtime config.')} />
            <ObservabilityCard name="OpenTelemetry" status={snapshot?.observability?.tracing.openTelemetryEnabled ? 'EXPORTING' : 'READY'} detail={snapshot?.observability?.tracing.exporterOtlpEndpoint ?? readRuntimeLink('VITE_OTEL_COLLECTOR_URL', 'Collector URL can be supplied by runtime config.')} />
            <ObservabilityCard name="Alert routes" status={snapshot?.observability?.alerting.sinks.some((sink) => sink.enabled) ? 'ENABLED' : 'CONFIGURED'} detail={formatAlertSinks(snapshot?.observability)} />
          </div>
          <pre className="code-block metrics-preview">{snapshot?.metrics.slice(0, 1600) || '# Metrics endpoint unavailable in this environment.'}</pre>
        </section>
      </section>

      <section className="page-grid two-columns">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>SLA and retention</h2>
              <p>Delivery deadlines, response objectives, availability targets, and archival policy.</p>
            </div>
          </div>
          <div className="summary-grid compact-summary">
            <div><strong>Delivery</strong><span>{formatMs(snapshot?.observability?.sla.deliveryDeadlineMs ?? 0)}</span></div>
            <div><strong>Response</strong><span>{formatMs(snapshot?.observability?.sla.responseTimeMs ?? 0)}</span></div>
            <div><strong>Availability</strong><span>{snapshot?.observability?.sla.availabilityTargetPercent ?? 0}%</span></div>
            <div><strong>Compliance</strong><span>{snapshot?.observability?.retention.complianceDays ?? 0} days</span></div>
            <div><strong>Archive</strong><span>{snapshot?.observability?.retention.archiveStorage ?? 'not configured'}</span></div>
            <div><strong>Warm store</strong><span>{snapshot?.observability?.retention.warmDays ?? 0} days</span></div>
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>Forensics</h2>
              <p>Replay analysis, execution reconstruction, and historical tracing.</p>
            </div>
          </div>
          <div className="stack-list">
            {(snapshot?.observability?.forensics.historicalTraces ?? []).slice(-5).map((trace) => (
              <div className="stack-item" key={trace.spanId}>
                <strong>{trace.name}</strong>
                <span className="inline-copy">
                  <span className="mono">{trace.correlationId ?? trace.traceId ?? trace.exchangeId ?? trace.spanId}</span>
                  <CopyButton value={trace.correlationId ?? trace.traceId ?? trace.exchangeId ?? trace.spanId} label="Copy trace identifier" />
                </span>
                <span>{trace.status} in {trace.durationMs} ms</span>
              </div>
            ))}
            {snapshot?.observability?.forensics.historicalTraces.length ? null : <EmptyState label="No historical traces sampled yet." />}
          </div>
        </section>
      </section>
      <ConfirmDialog state={dialog} onClose={() => setDialog(null)} />
    </div>
  );
}

function OperationalDestination({ title, detail, to, search }: { title: string; detail: string; to: string; search: string }) {
  return (
    <Link className="operations-card command-center-link" to={{ pathname: to, search }}>
      <strong>{title}</strong>
      <p>{detail}</p>
      <span>Open console &rarr;</span>
    </Link>
  );
}

function formatRuntimeSummary(value: string): string {
  const normalized = value.replace(/^(exg|flowexec|flow|doc)_/, '$1 ');
  return normalized.length > 42 ? `${normalized.slice(0, 42)}...` : normalized;
}

function formatOverviewTime(value: string): string {
  const elapsedMinutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60_000));
  if (elapsedMinutes < 1) return 'Just now';
  if (elapsedMinutes < 60) return `${elapsedMinutes}m ago`;
  const elapsedHours = Math.round(elapsedMinutes / 60);
  if (elapsedHours < 24) return `${elapsedHours}h ago`;
  return `${Math.round(elapsedHours / 24)}d ago`;
}

function Kpi({ label, value, detail, tone, trend }: {
  label: string;
  value: string | number;
  detail: string;
  tone: 'good' | 'warning' | 'danger' | 'neutral';
  trend?: ReactNode;
}) {
  return (
    <article className={`operations-card kpi-${tone}`}>
      <span className="operations-card-label">{label}</span>
      <strong>{value}</strong>
      {trend}
      <p>{detail}</p>
    </article>
  );
}

function permissionGranted(
  permission: Permission,
  grants: {
    canRetryExchange: boolean;
    canReplayExchange: boolean;
    canManageDeadLetter: boolean;
    canDeployFlow: boolean;
  },
): boolean {
  if (permission === 'integration.execution.retry') return grants.canRetryExchange;
  if (permission === 'integration.execution.replay') return grants.canReplayExchange;
  if (permission === 'integration.dlq.requeue') return grants.canManageDeadLetter;
  if (permission === 'flow.operations.execute') return grants.canDeployFlow;
  return false;
}

function PayloadViewer({
  value,
  compare,
  format,
  redact,
  diff,
}: {
  value: Record<string, unknown>;
  compare: Record<string, unknown>;
  format: PayloadFormat;
  redact: boolean;
  diff: boolean;
}) {
  const rendered = formatPayload(redact ? redactObject(value) : value, format);
  const compared = formatPayload(redact ? redactObject(compare) : compare, format);

  if (diff) {
    return (
      <div className="payload-diff">
        <pre className="code-block content-viewer">{rendered}</pre>
        <pre className="code-block content-viewer">{compared}</pre>
      </div>
    );
  }

  return <pre className="code-block content-viewer">{rendered}</pre>;
}

function ObservabilityCard({ name, status, detail }: { name: string; status: string; detail: string }) {
  return (
    <div className="stack-item">
      <strong>{name}</strong>
      <span><StatusBadge value={status} /> {detail}</span>
    </div>
  );
}

function buildGlobalDashboard(snapshot: OperationsSnapshot | null) {
  const exchanges = snapshot?.exchanges ?? [];
  const failed = exchanges.filter((exchange) => exchange.status === 'FAILED');
  const waitingRetry = exchanges.filter((exchange) => exchange.status === 'WAITING_RETRY');
  const deadLettered = exchanges.filter((exchange) => exchange.status === 'DEAD_LETTERED');
  const oneHourAgo = Date.now() - 60 * 60 * 1000;
  const recent = exchanges.filter((exchange) => new Date(exchange.createdAt).getTime() >= oneHourAgo);
  const slaBreaches = exchanges.filter((exchange) => {
    const ageMinutes = (Date.now() - new Date(exchange.createdAt).getTime()) / 60000;
    return ageMinutes > 15 && ['FAILED', 'IN_PROGRESS', 'WAITING_RETRY'].includes(exchange.status);
  }).length;
  const connectors = snapshot?.connectors ?? [];
  const healthyConnectors = connectors.filter((connector) => connector.health.status === 'HEALTHY').length;
  const activePartners = (snapshot?.partners ?? []).filter((partner) => partner.isActive).length;
  const denominator = Math.max(1, connectors.length + (snapshot?.partners.length ?? 0));

  return {
    exchangesPerSecond: recent.length / 3600,
    failures: failed.length,
    retries: waitingRetry.length,
    deadLettered: deadLettered.length,
    slaBreaches,
    partnerAvailability: Math.round(((healthyConnectors + activePartners) / denominator) * 100),
  };
}

const TREND_BUCKET_COUNT = 8;

// Sparkline/trend buckets computed entirely from the exchanges array the snapshot endpoint
// already returns (up to the 100 most recent) — no new backend call. See src/lib/timeBuckets.ts
// for the "most recent bucket vs. the one before it" trend definition and its caveats.
function buildExchangeTrendBuckets(snapshot: OperationsSnapshot | null) {
  const exchanges = snapshot?.exchanges ?? [];
  const bucket = (predicate: (exchange: ExchangeSummary) => boolean) =>
    bucketCountsByTime(exchanges.filter(predicate).map((exchange) => exchange.createdAt), TREND_BUCKET_COUNT, ONE_HOUR_MS);

  return {
    volume: bucket(() => true),
    failures: bucket((exchange) => exchange.status === 'FAILED'),
    retries: bucket((exchange) => exchange.status === 'WAITING_RETRY'),
    deadLettered: bucket((exchange) => exchange.status === 'DEAD_LETTERED'),
  };
}

function buildPartnerHealth(snapshot: OperationsSnapshot | null) {
  const exchanges = snapshot?.exchanges ?? [];
  const connectors = snapshot?.connectors ?? [];

  return (snapshot?.partners ?? []).map((partner) => {
    const partnerConnectors = connectors.filter((connector) => connector.partnerConfigId === partner.id);
    const healthy = partnerConnectors.filter((connector) => connector.health.status === 'HEALTHY').length;
    const failures = exchanges.filter((exchange) => exchange.partnerId === partner.id && exchange.status === 'FAILED').length;
    const expiry = readCertificateExpiry(partner);

    return {
      code: partner.code,
      name: partner.name,
      uptime: partnerConnectors.length ? Math.round((healthy / partnerConnectors.length) * 100) : partner.isActive ? 100 : 0,
      latencyMs: Number(partner.slaProfile?.['latencyMs'] ?? partner.settings?.['latencyMs'] ?? 0),
      failures,
      certificateStatus: expiry ? certificateStatus(expiry) : 'UNKNOWN',
    };
  });
}

function buildAlerts(snapshot: OperationsSnapshot | null, global: ReturnType<typeof buildGlobalDashboard>) {
  const alerts: Array<{ id: string; title: string; description: string; severity: 'warning' | 'danger' }> = [];
  for (const alert of snapshot?.observability?.alerting.activeAlerts ?? []) {
    alerts.push({
      id: alert.id,
      title: alert.title,
      description: alert.description,
      severity: alert.severity === 'critical' ? 'danger' : 'warning',
    });
  }
  if (global.slaBreaches > 0) {
    alerts.push({ id: 'sla', title: 'SLA violations detected', description: `${global.slaBreaches} exchanges are outside the runtime SLA threshold.`, severity: 'danger' });
  }
  if (global.deadLettered > 0) {
    alerts.push({ id: 'dlq', title: 'DLQ requires disposition', description: `${global.deadLettered} exchanges are currently dead-lettered.`, severity: 'danger' });
  }
  const unhealthy = (snapshot?.connectors ?? []).filter((connector) => ['DEGRADED', 'UNHEALTHY'].includes(connector.health.status));
  if (unhealthy.length > 0) {
    alerts.push({ id: 'connectors', title: 'Connector failures', description: `${unhealthy.length} connectors are degraded or unhealthy.`, severity: 'warning' });
  }
  if (global.partnerAvailability < 95) {
    alerts.push({ id: 'runtime', title: 'Runtime degradation', description: `Partner availability is ${global.partnerAvailability}%.`, severity: 'warning' });
  }
  return alerts;
}

function formatPayload(value: Record<string, unknown>, format: PayloadFormat): string {
  if (format === 'json') return JSON.stringify(value, null, 2);
  if (format === 'xml') return objectToXml('payload', value);
  return objectToEdifact(value);
}

function redactObject(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object') return {};
  if (Array.isArray(value)) return { items: redactSensitiveValue(value) as unknown[] };
  return redactSensitiveValue(value) as Record<string, unknown>;
}

function objectToXml(name: string, value: unknown): string {
  if (value === null || value === undefined) return `<${name} />`;
  if (typeof value !== 'object') return `<${name}>${escapeXml(String(value))}</${name}>`;
  if (Array.isArray(value)) return value.map((item) => objectToXml(name, item)).join('\n');
  const body = Object.entries(value).map(([key, entry]) => objectToXml(key, entry)).join('\n');
  return `<${name}>\n${body}\n</${name}>`;
}

function objectToEdifact(value: Record<string, unknown>): string {
  return Object.entries(value).map(([key, entry]) => `${key.toUpperCase()}+${String(typeof entry === 'object' ? JSON.stringify(entry) : entry)}'`).join('\n');
}

function escapeXml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function readCertificateExpiry(partner: PartnerRecord): string | undefined {
  const certificates = partner.securityProfile?.['certificates'];
  if (Array.isArray(certificates)) {
    const first = certificates[0] as Record<string, unknown> | undefined;
    return typeof first?.['expiresAt'] === 'string' ? first['expiresAt'] : undefined;
  }
  const rotationPolicy = partner.securityProfile?.['rotationPolicy'] as Record<string, unknown> | undefined;
  return typeof rotationPolicy?.['expiresAt'] === 'string' ? rotationPolicy['expiresAt'] : undefined;
}

function certificateStatus(expiresAt: string): string {
  const days = (new Date(expiresAt).getTime() - Date.now()) / 86400000;
  if (days < 0) return 'EXPIRED';
  if (days < 30) return 'EXPIRING';
  return 'VALID';
}

function parseMetricCount(metrics: string): number {
  return metrics.split('\n').filter((line) => line.startsWith('# HELP')).length;
}

function readRuntimeLink(key: string, fallback: string): string {
  const runtimeConfig = (window as Window & { __APP_CONFIG__?: Record<string, string> }).__APP_CONFIG__;
  return runtimeConfig?.[key] ?? fallback;
}

function formatMs(value: number): string {
  if (value >= 60000) return `${Math.round(value / 60000)} min`;
  if (value >= 1000) return `${Math.round(value / 1000)} sec`;
  return `${value} ms`;
}

function formatAlertSinks(snapshot: ObservabilityDashboardSnapshot | null | undefined): string {
  const sinks = snapshot?.alerting.sinks ?? [];
  if (!sinks.length) return 'Slack, email, and webhook routes can be supplied by runtime config.';
  return sinks.map((sink) => `${sink.type}:${sink.enabled ? 'on' : 'off'}`).join(' | ');
}

function actionLabel(action: 'retry' | 'replay' | 'quarantine' | 'annotate'): string {
  switch (action) {
    case 'retry':
      return 'Operator retry';
    case 'replay':
      return 'Operator replay';
    case 'quarantine':
      return 'Quarantine for investigation';
    case 'annotate':
      return 'Operator annotation';
  }
}
